import React, { useState } from 'react';
import { ChevronDown, Smartphone, BellRing, HelpCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

// Same content as the FAQ section "Installer TRADEX AI comme une vraie app".
// Shared so Notifications.js and FAQ.js stay in sync.
export const PWA_INSTALL_ITEMS = [
  {
    q: 'Pourquoi installer TRADEX AI comme une application ?',
    a: "Une fois installée en PWA (Progressive Web App) sur votre écran d'accueil, TRADEX AI se comporte comme une véritable application native : icône dédiée, lancement plein écran (sans barre d'adresse), et surtout **réception des notifications push en temps réel même quand l'app est fermée**. Vous ne loupez plus jamais un TP touché, un SL déclenché ou un setup Scan Auto détecté.",
  },
  {
    q: 'Comment installer TRADEX AI sur iPhone (iOS) ?',
    a: "1) Ouvrez tradex-ai.fr dans **Safari** (obligatoire, ça ne marche pas depuis Chrome/Firefox sur iOS).\n2) Appuyez sur l'icône **Partager** (carré avec flèche vers le haut) en bas de l'écran.\n3) Faites défiler et sélectionnez **\"Sur l'écran d'accueil\"**.\n4) Confirmez avec \"Ajouter\" en haut à droite.\n\nL'icône TRADEX AI apparaît sur votre écran d'accueil. Ouvrez-la comme une app normale. Au premier lancement, acceptez la demande d'autorisation des notifications — c'est essentiel pour recevoir les alertes en arrière-plan.",
  },
  {
    q: 'Comment installer TRADEX AI sur Android ?',
    a: "1) Ouvrez tradex-ai.fr dans **Chrome** (ou tout navigateur compatible PWA).\n2) Un bandeau **\"Ajouter TRADEX AI à l'écran d'accueil\"** apparaît automatiquement au bas de la page — appuyez sur **\"Installer\"**.\n3) Si le bandeau ne s'affiche pas : menu ⋮ en haut à droite → **\"Installer l'application\"** ou **\"Ajouter à l'écran d'accueil\"**.\n4) Confirmez.\n\nL'app s'installe dans votre tiroir d'applications comme n'importe quelle app. Acceptez les notifications au premier lancement pour recevoir les alertes push en arrière-plan.",
  },
  {
    q: "Les notifications marchent-elles vraiment quand l'app est fermée ?",
    a: "Oui, à deux conditions strictes :\n\n1) L'app doit être **installée via l'écran d'accueil** (pas juste ouverte dans un onglet du navigateur).\n2) Vous devez avoir **autorisé les notifications** au premier lancement (ou depuis les réglages système de votre téléphone : iOS Réglages → Notifications → TRADEX AI / Android Paramètres → Applications → TRADEX AI → Notifications).\n\nUne fois configuré, vous recevrez les alertes TP/SL touchés, setups Scan Auto détectés et changements de statut d'abonnement, même téléphone verrouillé.",
  },
  {
    q: "J'ai refusé les notifications au premier lancement, comment les réactiver ?",
    a: "**iPhone** : Réglages iOS → Notifications → cherchez \"TRADEX AI\" → activez \"Autoriser les notifications\" + \"Sons\" + \"Badges\".\n\n**Android** : Paramètres → Applications → TRADEX AI → Notifications → activez toutes les catégories.\n\nEnsuite, ouvrez TRADEX AI → **Mes paramètres → Notifications** → activez le toggle \"Notifications push\" pour re-synchroniser.",
  },
  {
    q: "L'installation prend-elle de la place sur mon téléphone ?",
    a: "Très peu : environ **10-15 Mo** (contre 80-150 Mo pour une app native équivalente). Les données de trades restent dans le cloud, donc l'app locale reste légère. C'est un des gros avantages de la technologie PWA.",
  },
  {
    q: 'Puis-je installer TRADEX AI sur mon ordinateur aussi ?',
    a: "Oui, sur **Chrome desktop / Edge / Brave / Opera** : cliquez sur l'icône **+** ou l'ordinateur avec flèche dans la barre d'adresse (à droite du champ URL), ou menu ⋮ → **\"Installer TRADEX AI\"**. L'app apparaît dans votre Applications (Mac) ou menu Démarrer (Windows) et s'ouvre dans sa propre fenêtre sans barre d'adresse — pratique pour l'utiliser côte-à-côte avec TradingView.",
  },
];

const formatAnswer = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="text-foreground font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const PWAInstallGuide = ({ defaultOpen = null, collapsible = true }) => {
  const [openIndex, setOpenIndex] = useState(defaultOpen);
  const [expanded, setExpanded] = useState(!collapsible);

  return (
    <div
      className="glass rounded-2xl border border-primary/20 overflow-hidden"
      data-testid="pwa-install-guide"
    >
      {/* Header */}
      <div className="p-5 sm:p-6 bg-gradient-to-br from-primary/10 to-emerald-500/5 border-b border-primary/10">
        <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
          <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="text-lg sm:text-xl font-semibold text-foreground">
                Installer TRADEX AI comme une vraie app
              </h2>
              <Badge className="bg-primary/20 text-primary border-primary/30">
                <BellRing className="w-3 h-3 mr-1" />
                Indispensable pour les push
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Pour recevoir vos alertes <strong className="text-foreground">TP / SL / Scan Auto en temps réel</strong> même
              téléphone verrouillé, TRADEX AI doit être installé depuis l&apos;écran d&apos;accueil (PWA).
            </p>
          </div>
          {collapsible && (
            <Button
              variant={expanded ? 'ghost' : 'outline'}
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              className={`flex-shrink-0 ${expanded ? '' : 'border-primary/40 text-primary hover:bg-primary/10'}`}
              data-testid="pwa-install-toggle-btn"
            >
              <HelpCircle className="w-4 h-4 mr-1.5" />
              {expanded ? 'Réduire' : 'En savoir plus'}
              <ChevronDown
                className={`w-4 h-4 ml-1.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </Button>
          )}
        </div>
      </div>

      {/* Accordion items */}
      {expanded && (
        <div className="divide-y divide-white/5" data-testid="pwa-install-items">
          {PWA_INSTALL_ITEMS.map((item, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div key={idx} data-testid={`pwa-faq-item-${idx}`}>
                <button
                  onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                  className="w-full flex items-center justify-between gap-4 p-4 sm:p-5 text-left hover:bg-white/[0.02] transition-colors"
                  data-testid={`pwa-faq-toggle-${idx}`}
                >
                  <span className="font-medium text-foreground text-sm sm:text-base">{item.q}</span>
                  <ChevronDown
                    className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${
                      isOpen ? 'rotate-180 text-primary' : ''
                    }`}
                  />
                </button>
                {isOpen && (
                  <div
                    className="px-4 pb-5 sm:px-5 text-sm text-muted-foreground leading-relaxed whitespace-pre-line"
                    data-testid={`pwa-faq-answer-${idx}`}
                  >
                    {formatAnswer(item.a)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PWAInstallGuide;
